import React, { useLayoutEffect, useRef, useState } from 'react';
import { TriangleAlert } from 'lucide-react';

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const fmtDate = (iso) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const fmtPct = (v) => `${Math.round(Number(v))}%`;

/**
 * Evolução da nota nos simulados (uma série). Linha de 2px, pontos com anel na cor da superfície,
 * linha de aprovação e tooltip com crosshair que segue o ponteiro até o simulado mais próximo.
 */
export function ScoreTrend({ points, threshold = 61, onSelect }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const height = 220;
  const pad = { top: 16, right: 48, bottom: 28, left: 36 };
  const w = Math.max(width - pad.left - pad.right, 50);
  const h = height - pad.top - pad.bottom;
  const n = points.length;
  const x = (i) => pad.left + (n === 1 ? w / 2 : (i / (n - 1)) * w);
  const y = (v) => pad.top + h - (Math.max(0, Math.min(100, Number(v))) / 100) * h;
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.score).toFixed(1)}`).join(' ');
  const area = n > 1 ? `${path} L${x(n - 1)},${y(0)} L${x(0)},${y(0)} Z` : null;
  const last = points[n - 1];

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let best = 0;
    points.forEach((_, i) => { if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i; });
    setHover(best);
  };

  const hp = hover != null ? points[hover] : null;

  return (
    <div className="ss-chart" ref={ref}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`Evolução da nota em ${n} ${n === 1 ? 'simulado' : 'simulados'}; última nota ${fmtPct(last.score)}`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        onClick={() => hp && onSelect?.(hp)}
        style={{ cursor: onSelect && hp ? 'pointer' : 'default' }}
      >
        {[0, 25, 50, 75, 100].map(v => (
          <g key={v}>
            <line className="ss-chart__grid" x1={pad.left} x2={pad.left + w} y1={y(v)} y2={y(v)} />
            <text className="ss-chart__tick" x={pad.left - 8} y={y(v)} dy="0.32em" textAnchor="end">{v}</text>
          </g>
        ))}
        <line className="ss-chart__threshold" x1={pad.left} x2={pad.left + w} y1={y(threshold)} y2={y(threshold)} />
        <text className="ss-chart__tick ss-chart__tick--strong" x={pad.left + w + 6} y={y(threshold)} dy="0.32em">{threshold}%</text>

        {area && <path className="ss-chart__area" d={area} />}
        {n > 1 && <path className="ss-chart__line" d={path} />}
        {hp && <line className="ss-chart__cross" x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + h} />}
        {points.map((p, i) => (
          <circle
            key={p.examId}
            className={`ss-chart__dot ${hover === i ? 'is-hover' : ''}`}
            cx={x(i)}
            cy={y(p.score)}
            r={hover === i ? 6 : 4.5}
          />
        ))}
        {/* Rótulo direto só no último ponto */}
        <text className="ss-chart__label" x={x(n - 1) + 10} y={y(last.score)} dy="0.32em">{fmtPct(last.score)}</text>

        <text className="ss-chart__tick" x={x(0)} y={height - 6} textAnchor={n === 1 ? 'middle' : 'start'}>{fmtDate(points[0].completedAt)}</text>
        {n > 1 && <text className="ss-chart__tick" x={x(n - 1)} y={height - 6} textAnchor="end">{fmtDate(last.completedAt)}</text>}
      </svg>

      {hp && (
        <div
          className="ss-chart__tooltip"
          style={{ left: Math.min(Math.max(x(hover), 90), width - 90), top: y(hp.score) - 12 }}
          role="status"
        >
          <strong>{fmtPct(hp.score)}</strong>
          <span>{hp.title}</span>
          <span>{new Date(hp.completedAt).toLocaleDateString('pt-BR')} · {hp.passed ? 'Aprovado' : 'Reprovado'}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Acerto por tópico em barras horizontais, do menor para o maior. Abaixo do limite, a barra
 * usa a cor de alerta e ganha o rótulo "ponto fraco" (a cor nunca é a única pista).
 */
export function TopicBars({ items, weakBelow = 0.7 }) {
  const sorted = [...items].sort((a, b) => a.accuracy - b.accuracy);
  return (
    <ul className="ss-bars" aria-label="Acerto por tópico">
      {sorted.map(t => {
        const pct = Math.round(t.accuracy * 100);
        const weak = t.accuracy < weakBelow;
        return (
          <li key={t.categoryId} className={`ss-bars__row ${weak ? 'is-weak' : ''}`}>
            <span className="ss-bars__name" title={t.name}>{t.name}</span>
            <span className="ss-bars__track" aria-hidden="true">
              <span className="ss-bars__fill" style={{ width: `${Math.max(pct, 2)}%` }} />
            </span>
            <span className="ss-bars__value">
              <strong>{pct}%</strong>
              <span>{t.correct}/{t.total}</span>
              {weak && (
                <span className="ss-bars__flag">
                  <TriangleAlert size={12} aria-hidden="true" /> ponto fraco
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
